// ---------------------------------------------------------------------------
// KYB Review Page — Comprehensive KYB case review workspace
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDebugHeaders } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useFormatters } from '../hooks/useFormatters';
import {
  UboChainDisplay,
  type UboDisplayItem,
  type UboChainData,
} from '../components/UboChainDisplay';
import {
  SupplementalDocsList,
  type SupplementalDocItem,
} from '../components/SupplementalDocsList';
import {
  BusinessScreeningResults,
  type BusinessScreeningData,
} from '../components/BusinessScreeningResults';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

interface Tab {
  key: string;
  label: string;
}

function TabNav({
  tabs,
  activeTab,
  onChange,
}: {
  tabs: Tab[];
  activeTab: string;
  onChange: (key: string) => void;
}) {
  const { t } = useTranslation('admin');
  return (
    <div
      className="border-b border-hairline"
      role="tablist"
      aria-label={t('kybReview.nav.sections')}
    >
      <nav className="flex -mb-px gap-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => onChange(tab.key)}
            className={`py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-ink-muted hover:text-ink hover:border-hairline-strong'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data types
// ---------------------------------------------------------------------------

interface BusinessEntityData {
  id: string;
  legal_name: string;
  registration_number: string | null;
  registration_country: string | null;
  entity_type: string | null;
  status: string;
  incorporation_date: string | null;
  case_id: string | null;
  created_at: string;
}

interface OfficerData {
  name: string;
  role: string;
  appointedDate: string | null;
  resignedDate: string | null;
  nationality: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function KybReviewPage() {
  const { t } = useTranslation('admin');
  const { entityId } = useParams<{ entityId: string }>();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [entity, setEntity] = useState<BusinessEntityData | null>(null);
  const [officers, setOfficers] = useState<OfficerData[]>([]);
  const [ubos, setUbos] = useState<UboDisplayItem[]>([]);
  const [uboChains, setUboChains] = useState<UboChainData[]>([]);
  const [supplementalDocs, setSupplementalDocs] = useState<SupplementalDocItem[]>([]);
  const [screening, setScreening] = useState<BusinessScreeningData | null>(null);

  const entityTypeLabels = t('kybReview.entityTypes', { returnObjects: true }) as Record<string, string>;

  const TABS: Tab[] = [
    { key: 'overview', label: t('kybReview.tabs.overview') },
    { key: 'officers', label: t('kybReview.tabs.officers') },
    { key: 'ubos', label: t('kybReview.tabs.ubos') },
    { key: 'documents', label: t('kybReview.tabs.documents') },
    { key: 'screening', label: t('kybReview.tabs.screening') },
    { key: 'decision', label: t('kybReview.tabs.decision') },
  ];

  // --- Load data ---
  const loadData = useCallback(async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = getDebugHeaders();

      // Fetch business entity
      const entityRes = await fetch(
        `${API_BASE}/v1/admin/business-entities/${entityId}`,
        { headers },
      );

      if (!entityRes.ok) {
        throw new Error(t('kybReview.errors.loadEntityFailed', { status: entityRes.status }));
      }

      const entityJson = (await entityRes.json()) as {
        data: BusinessEntityData;
      };
      setEntity(entityJson.data);

      // Fetch supplemental docs if we have a case ID
      if (entityJson.data.case_id) {
        const docsRes = await fetch(
          `${API_BASE}/v1/admin/cases/${entityJson.data.case_id}/supplemental-docs`,
          { headers },
        );
        if (docsRes.ok) {
          const docsJson = (await docsRes.json()) as {
            data: SupplementalDocItem[];
          };
          setSupplementalDocs(docsJson.data);
        }
      }

      // Officers: no dedicated officers API endpoint available yet.
      setOfficers([]);

      // Fetch UBO data from the ownership graph API
      try {
        const graphRes = await fetch(
          `${API_BASE}/v1/admin/business-entities/${entityId}/ownership-graph`,
          { headers },
        );
        if (graphRes.ok) {
          const graphJson = (await graphRes.json()) as {
            data: {
              ubos: Array<{ statementId: string; fullName: string | null; totalOwnership: number | null }>;
            };
          };
          const graph = graphJson.data;

          setUbos(
            graph.ubos.map((u) => ({
              id: u.statementId,
              name: u.fullName ?? t('common.unknown'),
              ownershipPercentage: u.totalOwnership ?? 0,
              nationality: null,
              verificationStatus: 'pending' as const,
              linkedCaseId: null,
            })),
          );

          setUboChains(
            graph.ubos.map((u) => ({
              rootEntityId: entityId,
              links: [
                { nodeId: entityId, name: entityJson.data.legal_name, nodeType: 'entity' as const, ownershipPercentage: 100 },
                { nodeId: u.statementId, name: u.fullName ?? t('common.unknown'), nodeType: 'person' as const, ownershipPercentage: u.totalOwnership ?? 0 },
              ],
              effectiveOwnership: u.totalOwnership ?? 0,
              uboNodeId: u.statementId,
            })),
          );
        } else {
          setUbos([]);
          setUboChains([]);
        }
      } catch {
        setUbos([]);
        setUboChains([]);
      }

      // Fetch screening data from API
      try {
        const screenRes = await fetch(
          `${API_BASE}/v1/admin/business-entities/${entityId}/screening`,
          { headers },
        );
        if (screenRes.ok) {
          const screenJson = (await screenRes.json()) as { data: BusinessScreeningData };
          setScreening(screenJson.data);
        } else {
          setScreening(null);
        }
      } catch {
        setScreening(null);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('kybReview.errors.loadFailed');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [entityId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Actions ---
  const handleRequestDoc = useCallback(
    async (documentType: string, reason: string) => {
      if (!entity?.case_id) return;
      try {
        const headers = {
          ...getDebugHeaders(),
          'Content-Type': 'application/json',
          'Idempotency-Key': crypto.randomUUID(),
        };
        const res = await fetch(
          `${API_BASE}/v1/admin/cases/${entity.case_id}/supplemental-docs`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ documentType, reason }),
          },
        );
        if (res.ok) {
          await loadData();
        }
      } catch {
        // Silently fail — production would show a toast
      }
    },
    [entity, loadData],
  );

  const handleDecision = useCallback(
    async (decisionType: string) => {
      if (!entity?.case_id) return;
      try {
        const headers = {
          ...getDebugHeaders(),
          'Content-Type': 'application/json',
          'Idempotency-Key': crypto.randomUUID(),
        };
        await fetch(
          `${API_BASE}/v1/admin/cases/${entity.case_id}/decisions`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ decisionType }),
          },
        );
        await loadData();
      } catch {
        // Silently fail
      }
    },
    [entity, loadData],
  );

  // --- Render ---
  if (!entityId) {
    return (
      <div className="p-6">
        <h1 className="font-display text-3xl text-ink mb-4">{t('kybReview.headings.title')}</h1>
        <div className="bg-warning-tint border border-warning/30 rounded-sm p-4 text-warning">
          <p className="text-sm">{t('kybReview.messages.noEntityId')}</p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingState rows={8} />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;
  if (!entity) return <ErrorState message={t('kybReview.errors.entityNotFound')} />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/cases"
          className="text-sm text-ink-muted hover:text-ink mb-2 inline-block"
        >
          &larr; {t('kybReview.nav.backToCases')}
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl text-ink">
              {entity.legal_name}
            </h1>
            <div className="mt-1 flex items-center gap-3 text-sm text-ink-muted">
              {entity.registration_number && (
                <span className="font-mono">{entity.registration_number}</span>
              )}
              {entity.registration_country && (
                <span>{entity.registration_country}</span>
              )}
              {entity.entity_type && (
                <span>
                  {entityTypeLabels[entity.entity_type] ?? entity.entity_type}
                </span>
              )}
            </div>
          </div>
          <StatusBadge value={entity.status} />
        </div>
      </div>

      {/* Tabs */}
      <TabNav tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <OverviewPanel entity={entity} officerCount={officers.length} uboCount={ubos.length} />
        )}
        {activeTab === 'officers' && <OfficersPanel officers={officers} />}
        {activeTab === 'ubos' && (
          <UboChainDisplay ubos={ubos} chains={uboChains} />
        )}
        {activeTab === 'documents' && (
          <SupplementalDocsList
            docs={supplementalDocs}
            onRequestNew={handleRequestDoc}
          />
        )}
        {activeTab === 'screening' && (
          <BusinessScreeningResults data={screening} />
        )}
        {activeTab === 'decision' && (
          <DecisionPanel onDecide={handleDecision} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-panels
// ---------------------------------------------------------------------------

function OverviewPanel({
  entity,
  officerCount,
  uboCount,
}: {
  entity: BusinessEntityData;
  officerCount: number;
  uboCount: number;
}) {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const entityTypeLabels = t('kybReview.entityTypes', { returnObjects: true }) as Record<string, string>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Business details card */}
      <div className="bg-surface-raised rounded-sm shadow p-5">
        <h3 className="text-sm font-semibold text-ink mb-4">
          {t('kybReview.headings.businessDetails')}
        </h3>
        <dl className="space-y-3">
          <div>
            <dt className="text-xs text-ink-muted">{t('kybReview.fields.legalName')}</dt>
            <dd className="text-sm font-medium text-ink">
              {entity.legal_name}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('kybReview.fields.registrationNumber')}</dt>
            <dd className="text-sm text-ink">
              {entity.registration_number ?? t('common.notAvailable')}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('kybReview.fields.jurisdiction')}</dt>
            <dd className="text-sm text-ink">
              {entity.registration_country ?? t('common.notAvailable')}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('kybReview.fields.entityType')}</dt>
            <dd className="text-sm text-ink">
              {entity.entity_type
                ? (entityTypeLabels[entity.entity_type] ?? entity.entity_type)
                : t('common.notAvailable')}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('kybReview.fields.incorporationDate')}</dt>
            <dd className="text-sm text-ink">
              {entity.incorporation_date
                ? f.date(entity.incorporation_date, { dateStyle: 'medium' })
                : t('common.notAvailable')}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('kybReview.fields.status')}</dt>
            <dd>
              <StatusBadge value={entity.status} />
            </dd>
          </div>
        </dl>
      </div>

      {/* Summary stats card */}
      <div className="bg-surface-raised rounded-sm shadow p-5">
        <h3 className="text-sm font-semibold text-ink mb-4">{t('kybReview.headings.summary')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-sm bg-primary-tint p-4 text-center">
            <p className="font-mono text-2xl font-medium text-primary tabular-nums">{officerCount}</p>
            <p className="text-xs text-primary">{t('kybReview.summary.officers')}</p>
          </div>
          <div className="rounded-sm bg-info-tint p-4 text-center">
            <p className="font-mono text-2xl font-medium text-info tabular-nums">{uboCount}</p>
            <p className="text-xs text-info">{t('kybReview.summary.ubosIdentified')}</p>
          </div>
          <div className="rounded-sm bg-surface-sunken p-4 text-center">
            <p className="font-mono text-2xl font-medium text-ink tabular-nums">
              {entity.case_id ? '1' : '0'}
            </p>
            <p className="text-xs text-ink-muted">{t('kybReview.summary.linkedCases')}</p>
          </div>
          <div className="rounded-sm bg-primary-tint p-4 text-center">
            <p className="font-mono text-2xl font-medium text-primary tabular-nums">
              {entity.registration_number ? t('common.yes') : t('common.no')}
            </p>
            <p className="text-xs text-primary">{t('kybReview.summary.registryVerified')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function OfficersPanel({ officers }: { officers: OfficerData[] }) {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const officerRoleLabels = t('kybReview.officerRoles', { returnObjects: true }) as Record<string, string>;

  if (officers.length === 0) {
    return (
      <div className="rounded-sm border border-hairline bg-surface-sunken p-6 text-center text-sm text-ink-muted">
        {t('kybReview.messages.officersUnavailable')}
      </div>
    );
  }

  return (
    <div className="bg-surface-raised rounded-sm shadow overflow-hidden">
      <div className="px-4 py-3 bg-surface-sunken border-b border-hairline">
        <h3 className="text-sm font-semibold text-ink">
          {t('kybReview.headings.officers', { count: officers.length })}
        </h3>
      </div>
      <ul className="divide-y divide-hairline" role="list" aria-label={t('kybReview.nav.officersListAria')}>
        {officers.map((officer, index) => (
          <li key={`officer-${index}`} className="px-4 py-3 hover:bg-surface-sunken">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-ink">
                  {officer.name}
                </p>
                <div className="mt-0.5 flex items-center gap-3 text-xs text-ink-muted">
                  <span>{officerRoleLabels[officer.role] ?? officer.role}</span>
                  {officer.nationality && <span>{officer.nationality}</span>}
                  {officer.appointedDate && (
                    <span>
                      {t('kybReview.messages.since', { date: f.date(officer.appointedDate, { dateStyle: 'medium' }) })}
                    </span>
                  )}
                </div>
              </div>
              {officer.resignedDate ? (
                <StatusBadge value="resigned" />
              ) : (
                <StatusBadge value="active" />
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const DECISION_STYLES: Record<string, string> = {
  approve: 'bg-primary hover:bg-primary-hover text-white focus:ring-primary',
  approve_with_conditions: 'bg-warning hover:bg-warning text-white focus:ring-warning',
  reject: 'bg-danger hover:bg-danger text-white focus:ring-danger',
  request_resubmission: 'bg-surface-sunken hover:bg-hairline-strong text-ink focus:ring-ink-muted',
};

const DECISION_VARIANTS: Record<string, 'danger' | 'warning'> = {
  approve: 'warning',
  approve_with_conditions: 'warning',
  reject: 'danger',
  request_resubmission: 'warning',
};

const DECISION_KEYS = ['approve', 'approve_with_conditions', 'reject', 'request_resubmission'] as const;

function DecisionPanel({
  onDecide,
}: {
  onDecide: (decisionType: string) => void;
}) {
  const { t } = useTranslation('admin');
  const [pendingDecision, setPendingDecision] = useState<string | null>(null);

  const config = pendingDecision
    ? {
        label: t(`kybReview.decisions.${pendingDecision}.label` as const),
        message: t(`kybReview.decisions.${pendingDecision}.message` as const),
        variant: DECISION_VARIANTS[pendingDecision] ?? 'danger',
      }
    : null;

  return (
    <div className="bg-surface-raised rounded-sm shadow p-6">
      <h3 className="text-sm font-semibold text-ink mb-4">
        {t('kybReview.headings.caseDecision')}
      </h3>
      <p className="text-sm text-ink-muted mb-6">
        {t('kybReview.messages.decisionIntro')}
      </p>
      <div className="flex flex-wrap gap-3">
        {DECISION_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setPendingDecision(key)}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${DECISION_STYLES[key]}`}
          >
            {t(`kybReview.decisions.${key}.label` as const)}
          </button>
        ))}
      </div>

      <ConfirmDialog
        open={pendingDecision !== null}
        title={t('kybReview.decisions.confirmTitle', { label: config?.label ?? '' })}
        message={config?.message ?? ''}
        confirmLabel={config?.label ?? t('kybReview.decisions.confirmFallback')}
        variant={config?.variant ?? 'danger'}
        onConfirm={() => {
          if (pendingDecision) {
            onDecide(pendingDecision);
          }
          setPendingDecision(null);
        }}
        onCancel={() => setPendingDecision(null)}
      />
    </div>
  );
}
